import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { NarrativeReport } from '../components/NarrativeReport';

interface AgendaItem {
  id: string;
  text: string;
  isGroupHeader?: boolean;
  covered?: boolean;
}

interface SpeakerStat {
  label: string;
  talkTimeMs: number;
  talkRatioPct: number;
}

interface PerformanceStats {
  avgWpm: number;
  userTalkRatioPct: number;
  totalWords: number;
  totalTalkTimeMs: number;
  speakerCount: number;
  speakerStats: Record<string, SpeakerStat>;
}

interface SavedActionItem {
  id: string;
  text: string;
  owner: 'user' | 'prospect';
  detectedAt?: number;
  completed: boolean;
}

interface SavedCoachingAlert {
  type: string;
  message: string;
  timestamp: number;
  value?: number;
}

interface SessionMetadata {
  performanceStats?: PerformanceStats;
  actionItems?: SavedActionItem[];
  coachingAlerts?: SavedCoachingAlert[];
}

// New metrics structure from normalized tables
interface SessionMetrics {
  performanceStats: PerformanceStats | null;
  actionItems: SavedActionItem[];
  coachingAlerts: SavedCoachingAlert[];
}

interface RehearsalQuestion {
  id: string;
  questionText: string;
  questionType: string;
  triggerContext?: string;
  responseText?: string;
  skipped: boolean;
  score?: string;
  feedback?: string;
  askedAt: string;
  respondedAt?: string;
}

interface RehearsalSession {
  id: string;
  difficulty: string;
  status: string;
  overallScore?: string;
  questionsTotal: number;
  questionsAnswered: number;
  questionsSkipped: number;
  questions: RehearsalQuestion[];
}

interface Session {
  id: string;
  title: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  coverageScore: string | null;
  itemsTotal: number | null;
  itemsCovered: number | null;
  agenda: AgendaItem[];
  metadata?: SessionMetadata;
  metrics?: SessionMetrics; // New normalized tables
  rehearsalSession?: RehearsalSession;
}

interface Transcript {
  segments: Array<{
    text: string;
    isFinal: boolean;
    timestamp?: number;
    speaker?: number;
  }>;
  wordCount: number;
}

export function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, delete: del } = useApi();

  const [session, setSession] = useState<Session | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [transcriptNeedsUpgrade, setTranscriptNeedsUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    async function fetchSession() {
      if (!id) return;

      try {
        setLoading(true);
        const data = await get<Session>(`/sessions/${id}`);
        setSession(data);

        // Try to fetch transcript (may fail if not available for tier or not stored)
        try {
          const transcriptData = await get<Transcript>(`/sessions/${id}/transcript`);
          setTranscript(transcriptData);
        } catch (err) {
          // Check if it's a 403 (upgrade needed) vs 404 (not found)
          const errorMessage = err instanceof Error ? err.message : '';
          if (errorMessage.includes('Upgrade') || errorMessage.includes('not available')) {
            setTranscriptNeedsUpgrade(true);
          }
          // Otherwise transcript just doesn't exist - that's OK
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
      } finally {
        setLoading(false);
      }
    }

    fetchSession();
  }, [id, get]);

  async function handleDelete() {
    if (!confirm('Are you sure you want to delete this session? This cannot be undone.')) {
      return;
    }

    try {
      await del(`/sessions/${id}`);
      navigate('/sessions');
    } catch (err) {
      alert('Failed to delete session');
    }
  }

  function formatDuration(ms: number | null): string {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="space-y-4">
        <Link to="/sessions" className="text-green-400 hover:text-green-300">
          &larr; Back to Sessions
        </Link>
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
          {error || 'Session not found'}
        </div>
      </div>
    );
  }

  const score = Number(session.coverageScore ?? 0);
  const date = new Date(session.startedAt);
  const scoreColor =
    score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';

  // Separate covered and missed items
  const trackableItems = session.agenda.filter((item) => !item.isGroupHeader);
  const coveredCount = session.itemsCovered ?? 0;
  const missedCount = trackableItems.length - coveredCount;

  // Use new metrics if available, fall back to metadata for backwards compatibility
  const performanceStats = session.metrics?.performanceStats ?? session.metadata?.performanceStats;
  const actionItems = session.metrics?.actionItems ?? session.metadata?.actionItems ?? [];
  // Coaching alerts are stored but not displayed in UI yet
  const _coachingAlerts = session.metrics?.coachingAlerts ?? session.metadata?.coachingAlerts ?? [];
  void _coachingAlerts; // Suppress unused variable warning

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/sessions" className="text-green-400 hover:text-green-300 text-sm">
            &larr; Back to Sessions
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-white">
            {session.title || 'Untitled Session'}
          </h1>
          <p className="mt-1 text-slate-400">
            {date.toLocaleDateString()} at{' '}
            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button
          onClick={handleDelete}
          className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm font-medium rounded-md border border-red-500/30"
        >
          Delete Session
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-slate-400">Coverage Score</p>
          <p className={`text-3xl font-bold ${scoreColor}`}>{Math.round(score)}%</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-slate-400">Duration</p>
          <p className="text-3xl font-bold text-white">{formatDuration(session.durationMs)}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-slate-400">Items Covered</p>
          <p className="text-3xl font-bold text-green-500">{coveredCount}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-slate-400">Items Missed</p>
          <p className="text-3xl font-bold text-red-500">{missedCount}</p>
        </div>
      </div>

      {/* Performance Stats (if available) */}
      {performanceStats && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Performance Stats</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Words Per Minute */}
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-1">Avg. WPM</p>
                <p className={`text-2xl font-bold ${
                  performanceStats.avgWpm > 170 ? 'text-red-400' :
                  performanceStats.avgWpm > 150 ? 'text-yellow-400' :
                  performanceStats.avgWpm > 110 ? 'text-green-400' :
                  performanceStats.avgWpm > 0 ? 'text-blue-400' : 'text-slate-500'
                }`}>
                  {performanceStats.avgWpm || '—'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {performanceStats.avgWpm > 170 ? 'Too fast' :
                   performanceStats.avgWpm > 150 ? 'Fast' :
                   performanceStats.avgWpm > 110 ? 'Good pace' :
                   performanceStats.avgWpm > 0 ? 'Slow' : 'No data'}
                </p>
              </div>

              {/* Talk Ratio - hidden for rehearsal sessions since user talks to computer */}
              {!session.rehearsalSession && (
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-1">Your Talk Ratio</p>
                <p className={`text-2xl font-bold ${
                  performanceStats.userTalkRatioPct > 70 ? 'text-red-400' :
                  performanceStats.userTalkRatioPct > 60 ? 'text-yellow-400' :
                  performanceStats.userTalkRatioPct >= 30 ? 'text-green-400' : 'text-blue-400'
                }`}>
                  {performanceStats.userTalkRatioPct}%
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {performanceStats.userTalkRatioPct > 70 ? 'Talk less, listen more' :
                   performanceStats.userTalkRatioPct > 60 ? 'Slight imbalance' :
                   performanceStats.userTalkRatioPct >= 30 ? 'Good balance' : 'Share more'}
                </p>
              </div>
              )}

              {/* Total Words */}
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-1">Total Words</p>
                <p className="text-2xl font-bold text-white">
                  {performanceStats.totalWords.toLocaleString()}
                </p>
                <p className="text-xs text-slate-500 mt-1">spoken during session</p>
              </div>

              {/* Speaker Count - hidden for rehearsal sessions */}
              {!session.rehearsalSession && (
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-1">Speakers Detected</p>
                <p className="text-2xl font-bold text-white">
                  {performanceStats.speakerCount}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {performanceStats.speakerCount > 1 ? 'via diarization' : 'single speaker'}
                </p>
              </div>
              )}
            </div>

            {/* Speaker Breakdown (if multiple speakers, not for rehearsal) */}
            {!session.rehearsalSession && performanceStats.speakerCount > 1 && (
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Speaker Breakdown</h3>
                <div className="space-y-2">
                  {Object.entries(performanceStats.speakerStats).map(([speakerId, stat]) => (
                    <div key={speakerId} className="flex items-center gap-3">
                      <span className="text-sm text-slate-400 w-20">{stat.label}</span>
                      <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${stat.label === 'You' ? 'bg-blue-500' : 'bg-slate-500'}`}
                          style={{ width: `${stat.talkRatioPct}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400 w-16 text-right">{stat.talkRatioPct}%</span>
                      <span className="text-xs text-slate-500 w-20 text-right">
                        {Math.round(stat.talkTimeMs / 1000)}s
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Items (if available) */}
      {actionItems.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Action Items</h2>
            <p className="text-sm text-slate-400">{actionItems.length} captured during session</p>
          </div>
          <div className="divide-y divide-slate-700">
            {actionItems.map((item) => (
              <div key={item.id} className="px-6 py-3 flex items-start gap-3">
                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs mt-0.5 ${
                  item.completed ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
                }`}>
                  {item.completed ? '✓' : '○'}
                </span>
                <div className="flex-1">
                  <p className={`text-sm ${item.completed ? 'text-slate-400 line-through' : 'text-white'}`}>
                    {item.text}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {item.owner === 'user' ? 'Your action' : "Prospect's action"}
                    {item.detectedAt && ` • Captured at ${Math.floor(item.detectedAt / 60)}:${String(Math.floor(item.detectedAt % 60)).padStart(2, '0')}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agenda Items */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Agenda Items</h2>
          <p className="text-sm text-slate-400">
            {coveredCount} of {trackableItems.length} items covered
          </p>
        </div>
        <div className="divide-y divide-slate-700">
          {session.agenda.map((item, index) => {
            if (item.isGroupHeader) {
              return (
                <div key={item.id || index} className="px-6 py-3 bg-slate-900/50">
                  <p className="text-sm font-medium text-slate-300 uppercase tracking-wide">
                    {item.text}
                  </p>
                </div>
              );
            }

            // For now, we don't have per-item coverage info stored
            // So we'll show all items with a neutral style
            // In a future enhancement, we could store which items were covered
            const itemIndex = trackableItems.findIndex((t) => t.id === item.id);
            const isCovered = itemIndex < coveredCount;

            return (
              <div
                key={item.id || index}
                className={`px-6 py-3 flex items-center gap-3 ${
                  isCovered ? 'opacity-60' : ''
                }`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                    isCovered
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {isCovered ? '✓' : '✗'}
                </span>
                <span className={`${isCovered ? 'text-slate-400 line-through' : 'text-white'}`}>
                  {item.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Narrative Analysis Section */}
      {session.status === 'completed' && (
        <NarrativeReport sessionId={session.id} hasTranscript={!!transcript} />
      )}

      {/* Transcript Section */}
      {transcript && transcript.segments.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <div>
              <h2 className="text-lg font-semibold text-white">Transcript</h2>
              <p className="text-sm text-slate-400">{transcript.wordCount} words</p>
            </div>
            <span className="text-slate-400">{showTranscript ? '▲' : '▼'}</span>
          </button>
          {showTranscript && (
            <div className="px-6 py-4 border-t border-slate-700 max-h-96 overflow-y-auto">
              <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                {transcript.segments
                  .filter((s) => s.isFinal)
                  .map((s) => s.text)
                  .join(' ')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* No transcript message - different messages for upgrade needed vs not stored */}
      {!transcript && transcriptNeedsUpgrade && (
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6 text-center">
          <p className="text-slate-400">
            Transcript storage is available on Team plans.
          </p>
          <Link
            to="/settings/billing"
            className="mt-2 inline-block text-green-400 hover:text-green-300 text-sm"
          >
            Upgrade to access transcripts →
          </Link>
        </div>
      )}
      {!transcript && !transcriptNeedsUpgrade && (
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6 text-center">
          <p className="text-slate-400">
            No transcript was stored for this session.
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Transcripts are automatically saved when sessions end normally.
          </p>
        </div>
      )}

      {/* Rehearsal Questions (if this was a rehearsal session) */}
      {session.rehearsalSession && session.rehearsalSession.questions.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="text-blue-400">🎯</span>
                  Rehearsal Questions
                </h2>
                <p className="text-sm text-slate-400">
                  {session.rehearsalSession.difficulty.charAt(0).toUpperCase() + session.rehearsalSession.difficulty.slice(1)} mode
                  {' • '}
                  {session.rehearsalSession.questionsAnswered} answered,{' '}
                  {session.rehearsalSession.questionsSkipped} skipped
                </p>
              </div>
              {session.rehearsalSession.overallScore && (
                <div className="text-right">
                  <p className="text-sm text-slate-400">Overall Score</p>
                  <p className={`text-2xl font-bold ${
                    Number(session.rehearsalSession.overallScore) >= 80 ? 'text-green-400' :
                    Number(session.rehearsalSession.overallScore) >= 60 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {Math.round(Number(session.rehearsalSession.overallScore))}%
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="divide-y divide-slate-700">
            {session.rehearsalSession.questions.map((question, index) => (
              <div key={question.id} className="px-6 py-4">
                {/* Question Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                      Q{index + 1}
                    </span>
                    <span className="text-xs text-slate-500 capitalize">{question.questionType.replace('_', ' ')}</span>
                  </div>
                  {question.score ? (
                    <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                      Number(question.score) >= 80 ? 'bg-green-500/20 text-green-400' :
                      Number(question.score) >= 60 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {Math.round(Number(question.score))}%
                    </span>
                  ) : question.skipped ? (
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                      Skipped
                    </span>
                  ) : null}
                </div>

                {/* Question Text */}
                <p className="text-white mb-3">{question.questionText}</p>

                {/* Response */}
                {question.responseText && (
                  <div className="mb-3 p-3 bg-slate-900/50 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Your Response:</p>
                    <p className="text-sm text-slate-300">{question.responseText}</p>
                  </div>
                )}

                {/* Feedback */}
                {question.feedback && (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-xs text-blue-400 mb-1">Feedback:</p>
                    <p className="text-sm text-slate-300">{question.feedback}</p>
                  </div>
                )}

                {/* Trigger Context (optional, collapsed) */}
                {question.triggerContext && (
                  <details className="mt-2">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
                      View trigger context
                    </summary>
                    <p className="mt-1 text-xs text-slate-500 italic pl-2 border-l border-slate-700">
                      "{question.triggerContext}"
                    </p>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
